export const defaultCategories = ["profile", "emails", "phones", "addresses", "documents", "security", "preferences"];

export const buildDraftStorageKey = (userId: string, appId: string, appObjectId: string) =>
  `pd.id.express.personal-data.draft.${userId}.${appId}.${appObjectId}`;

export const buildPersonalDataLoadUrl = ({
  baseUrl,
}: {
  baseUrl: string;
  userId: string;
  appId: string;
  appObjectId?: string;
}) => {
  const url = new URL(`${baseUrl.replace(/\/$/, "")}/personal_data`);

  url.searchParams.set("current_page", "1");
  url.searchParams.set("items_per_page", "1");
  url.searchParams.set("status", "enabled");

  return url.toString();
};

export const sanitizeTheme = (theme: string | null | undefined): "light" | "dark" =>
  theme === "light" ? "light" : "dark";

export const isMetadataKey = (key: string) => key === "metadata" || key === "__metadata";

const normalizeExtractedValue = (key: string, value: unknown) => {
  if (typeof value === "string" && key.toLowerCase().includes("date")) {
    const dateMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (dateMatch) return dateMatch[0];
  }

  return value;
};

export const extractValues = (obj: any): any => {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(extractValues);

  const result: Record<string, any> = {};

  Object.entries(obj).forEach(([key, val]) => {
    if (isMetadataKey(key)) return;

    if (val && typeof val === "object" && "value" in val) {
      result[key] = normalizeExtractedValue(key, (val as { value: unknown }).value);
      return;
    }

    if (val && typeof val === "object") {
      const nested = extractValues(val);
      result[key] = nested;
      return;
    }

    result[key] = normalizeExtractedValue(key, val);
  });

  return result;
};

export const extractFieldMetadata = (obj: any): any => {
  if (!obj || typeof obj !== "object") return {};

  const result: Record<string, any> = {};

  Object.entries(obj).forEach(([key, val]) => {
    if (!val || typeof val !== "object") return;

    const fieldValue = val as Record<string, any>;
    const nested = extractFieldMetadata(fieldValue);

    if (fieldValue.metadata && typeof fieldValue.metadata === "object") {
      nested.__metadata = fieldValue.metadata;
    }

    if (Object.keys(nested).length > 0) {
      result[key] = nested;
    }
  });

  return result;
};

export const deepMerge = (base: Record<string, any>, incoming: Record<string, any>): Record<string, any> => {
  const result = { ...base };

  Object.entries(incoming || {}).forEach(([key, value]) => {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      base?.[key] &&
      typeof base[key] === "object" &&
      !Array.isArray(base[key])
    ) {
      result[key] = deepMerge(base[key], value);
      return;
    }

    result[key] = value;
  });

  return result;
};

export const scopePersonalDataPayload = (
  data: Record<string, any>,
  category: string
): Record<string, any> => {
  if (!category || !data || !Object.prototype.hasOwnProperty.call(data, category)) {
    return {};
  }

  return { [category]: data[category] };
};

export const scopeEmailFieldsPayload = (
  emails: Record<string, any>,
  fields: string[]
): Record<string, any> =>
  fields.reduce<Record<string, any>>((payload, field) => {
    if (emails && Object.prototype.hasOwnProperty.call(emails, field)) {
      payload[field] = emails[field];
    }
    return payload;
  }, {});

export const replacePersonalDataCategory = (
  data: Record<string, any>,
  category: string,
  categoryData: Record<string, any>
): Record<string, any> => ({
  ...data,
  [category]: categoryData,
});

export const computeDiff = (current: any, original: any): any => {
  const diff: Record<string, any> = {};
  const keys = new Set([
    ...Object.keys(current || {}),
    ...Object.keys(original || {}),
  ]);

  keys.forEach((key) => {
    if (isMetadataKey(key)) return;

    const value = current?.[key];
    const hasCurrentKey = current && Object.prototype.hasOwnProperty.call(current, key);
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const subDiff = computeDiff(value, original?.[key] || {});
      if (Object.keys(subDiff).length > 0) diff[key] = subDiff;
      return;
    }

    if (!hasCurrentKey && original && Object.prototype.hasOwnProperty.call(original, key)) {
      diff[key] = null;
      return;
    }

    if (value !== original?.[key]) {
      diff[key] = value;
    }
  });

  return diff;
};

const hasAddressValue = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const sanitizeAddressFormData = (data: Record<string, any>) => {
  if (!data?.addresses || typeof data.addresses !== "object") return data;

  const addresses = Object.entries(data.addresses).reduce<Record<string, any>>(
    (result, [addressType, rawAddress]) => {
      if (!rawAddress || typeof rawAddress !== "object") return result;

      const address = Object.entries(rawAddress).reduce<Record<string, any>>(
        (fields, [field, value]) => {
          if (isMetadataKey(field) || !hasAddressValue(value)) return fields;
          fields[field] = typeof value === "string" ? value.trim() : value;
          return fields;
        },
        {}
      );

      if (Object.keys(address).length > 0) result[addressType] = address;
      return result;
    },
    {}
  );

  return { ...data, addresses };
};

const requiredAddressFields = ["country", "city", "street", "house", "postalCode"];

export const getMissingAddressFields = (data: Record<string, any>) => {
  const addresses = data?.addresses;
  if (!addresses || typeof addresses !== "object") return [];

  return Object.entries(addresses).flatMap(([addressType, rawAddress]) => {
    if (!rawAddress || typeof rawAddress !== "object") return [];
    const address = rawAddress as Record<string, unknown>;
    const hasAnyValue = Object.entries(address)
      .filter(([field]) => !isMetadataKey(field))
      .some(([, value]) => hasAddressValue(value));

    if (!hasAnyValue) return [];
    return requiredAddressFields
      .filter((field) => !hasAddressValue(address[field]))
      .map((field) => `addresses.${addressType}.${field}`);
  });
};

export const countFilledFields = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  if (Array.isArray(value)) return value.reduce((sum, item) => sum + countFilledFields(item), 0);
  if (typeof value !== "object") return 1;

  return Object.entries(value)
    .filter(([key]) => !isMetadataKey(key))
    .reduce<number>((sum, [, item]) => sum + countFilledFields(item), 0);
};

export const countTotalFields = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (Array.isArray(value)) return value.reduce((sum, item) => sum + countTotalFields(item), 0);
  if (typeof value !== "object") return 1;

  return Object.entries(value)
    .filter(([key]) => !isMetadataKey(key))
    .reduce<number>((sum, [, item]) => sum + countTotalFields(item), 0);
};

export const formatDisplayName = (profile: Record<string, any> = {}, fallback = "ID profile") => {
  return (
    profile.displayName ||
    [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ").trim() ||
    profile.nickname ||
    fallback
  );
};

export const getPrimaryEmail = (emails: Record<string, any> = {}) =>
  emails.primary || emails.personal || emails.work || emails.backup || "not-set@id.express";

export const getPrimaryPhone = (phones: Record<string, any> = {}) =>
  phones.mobile || phones.work || phones.home || phones.emergency || "+000 000 000 000";

export const estimateProfileCompleteness = (data: Record<string, any>) => {
  const filled = countFilledFields(data);
  const total = Math.max(countTotalFields(data), 1);
  return Math.min(100, Math.round((filled / total) * 100));
};

const personalDataDraftVersion = 2;

/** KYC decisions and evidence are server state, never ordinary profile drafts. */
const withoutVerificationDraft = (value: Record<string, any>): Record<string, any> => {
  if (!value.profile || typeof value.profile !== "object" || Array.isArray(value.profile)
    || !Object.prototype.hasOwnProperty.call(value.profile, "accountVerification")) return value;
  const { accountVerification, ...profile } = value.profile;
  const { profile: previousProfile, ...otherCategories } = value;
  return Object.keys(profile).length ? { ...otherCategories, profile } : otherCategories;
};

export const readDraft = (storageKey: string) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (
      parsed?.version !== personalDataDraftVersion ||
      !parsed.data ||
      typeof parsed.data !== "object" ||
      Array.isArray(parsed.data)
    ) {
      localStorage.removeItem(storageKey);
      return null;
    }

    const data = withoutVerificationDraft(parsed.data);
    if (!Object.keys(data).length) {
      localStorage.removeItem(storageKey);
      return null;
    }
    if (data !== parsed.data) {
      localStorage.setItem(storageKey, JSON.stringify({ version: personalDataDraftVersion, data }));
    }
    return data;
  } catch (error) {
    console.error("Unable to read personal data draft", error);
    return null;
  }
};

export const writeDraft = (storageKey: string, value: Record<string, any>) => {
  try {
    const data = withoutVerificationDraft(value);
    if (!Object.keys(data).length) {
      localStorage.removeItem(storageKey);
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify({
      version: personalDataDraftVersion,
      data,
    }));
  } catch (error) {
    console.error("Unable to write personal data draft", error);
  }
};

export const clearDraft = (storageKey: string) => {
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error("Unable to clear personal data draft", error);
  }
};
